import JSZip from "jszip";

// ────────────────────────────────────────────────────────────────────────────
// Clal (כלל חברה לביטוח) production files arrive as self-extracting .exe archives
// from the vault. They are actually ZIP-type SFX (a stub prepended to a normal
// ZIP central directory), so JSZip.loadAsync() opens them directly in the browser
// — no 7-Zip / server-side extraction needed.
//
// Each .exe contains exactly ONE of three inner folders, by file type:
//   • "ממשק אחזקות"        → financial savings (חיסכון), 008 XML (.DAT, SUG-MIMSHAK=1) → LOAD via existing holdings pipeline
//   • "אפקס חיים - בריאות"  → health insurance (בריאות), proprietary Apex fixed-width  → LOAD via clalApexParser
//   • "אפקס חיים"          → life insurance (ביטוח חיים), proprietary Apex             → LOAD via clalApexParser
// ────────────────────────────────────────────────────────────────────────────

export const CLAL_KIND = {
  HOLDINGS: "holdings", // ממשק אחזקות
  HEALTH: "health", // אפקס חיים - בריאות
  LIFE: "life", // אפקס חיים  (skipped)
  UNKNOWN: "unknown",
};

const FOLDER_HOLDINGS = "ממשק אחזקות";
const FOLDER_HEALTH = "אפקס חיים - בריאות";
const FOLDER_LIFE = "אפקס חיים"; // substring of FOLDER_HEALTH — test health first!

// Is this a Clal SFX .exe (by name)? Vault files are named "תיבה_<box>_..._מתאריך_...exe".
export function isClalExe(file) {
  return /\.exe$/i.test(file?.name || "") && /^תיבה[_\s]/.test(file?.name || "");
}

function classifyPath(path) {
  // Order matters: "אפקס חיים - בריאות" contains "אפקס חיים" as a substring.
  if (path.includes(FOLDER_HOLDINGS)) return CLAL_KIND.HOLDINGS;
  if (path.includes(FOLDER_HEALTH)) return CLAL_KIND.HEALTH;
  if (path.includes(FOLDER_LIFE)) return CLAL_KIND.LIFE;
  return CLAL_KIND.UNKNOWN;
}

// Last path segment (basename) — handles both "/" and "\" separators.
function baseName(path) {
  const parts = path.split(/[\\/]/);
  return parts[parts.length - 1] || path;
}

// Open a single Clal .exe and split its entries by inner folder.
// Returns:
//   {
//     fileName,
//     detectedKind,                 // dominant kind in the archive
//     holdings: [{ name, arrayBuffer }],   // .DAT/.xml under "ממשק אחזקות"
//     health:   { [ext]: arrayBuffer, _baseName } | null,  // Apex files under "אפקס חיים - בריאות"
//     life:     { [ext]: arrayBuffer, _baseName } | null,  // Apex files under "אפקס חיים" (same format)
//   }
export async function classifyClalExe(file) {
  const zip = await JSZip.loadAsync(file);
  const result = {
    fileName: file.name,
    detectedKind: CLAL_KIND.UNKNOWN,
    holdings: [],
    health: null,
    life: null,
    valueDate: null,
  };

  const entries = Object.values(zip.files).filter((e) => !e.dir);

  // Value date — every entry path contains a "YYYY.MM.DD HH-MM" folder
  // (e.g. ".../2026.05.03 08-22/...") = the production value date. Extract it
  // so health/life sources carry a date without the user setting one manually.
  for (const e of entries) {
    const m = e.name.match(/(\d{4})\.(\d{2})\.(\d{2})/);
    if (m) { result.valueDate = `${m[1]}-${m[2]}-${m[3]}`; break; }
  }

  // Fallback: If inner folders don't have the date, try parsing from the .exe filename itself
  // e.g. "תיבה_..._מתאריך_04-05-2026_בשעה_..."
  if (!result.valueDate && file?.name) {
    const fm = file.name.match(/מתאריך_(\d{2})-(\d{2})-(\d{4})/);
    if (fm) { result.valueDate = `${fm[3]}-${fm[2]}-${fm[1]}`; }
  }

  // Health (אפקס חיים - בריאות) and Life (אפקס חיים) share the same Apex
  // fixed-width format (.POL/.MEV/.TAR/.SGB…). Collect each into an ext→buffer map.
  const healthByExt = {};
  let healthBase = null;
  const lifeByExt = {};
  let lifeBase = null;

  for (const entry of entries) {
    const kind = classifyPath(entry.name);
    if (kind === CLAL_KIND.HOLDINGS) {
      const arrayBuffer = await entry.async("arraybuffer");
      result.holdings.push({ name: entry.name, arrayBuffer });
    } else if (kind === CLAL_KIND.HEALTH || kind === CLAL_KIND.LIFE) {
      const bn = baseName(entry.name); // e.g. "75320.POL"
      const dot = bn.lastIndexOf(".");
      const ext = dot >= 0 ? bn.slice(dot + 1).toUpperCase() : "";
      if (!ext) continue;
      const buf = await entry.async("arraybuffer");
      if (kind === CLAL_KIND.HEALTH) {
        healthByExt[ext] = buf;
        healthBase = dot >= 0 ? bn.slice(0, dot) : bn;
      } else {
        lifeByExt[ext] = buf;
        lifeBase = dot >= 0 ? bn.slice(0, dot) : bn;
      }
    }
  }

  if (Object.keys(healthByExt).length) {
    healthByExt._baseName = healthBase;
    result.health = healthByExt;
  }
  if (Object.keys(lifeByExt).length) {
    lifeByExt._baseName = lifeBase;
    result.life = lifeByExt;
  }

  // Dominant kind for UI labeling (one folder per .exe in practice).
  if (result.holdings.length) result.detectedKind = CLAL_KIND.HOLDINGS;
  else if (result.health) result.detectedKind = CLAL_KIND.HEALTH;
  else if (result.life) result.detectedKind = CLAL_KIND.LIFE;

  return result;
}

export const CLAL_KIND_LABEL = {
  [CLAL_KIND.HOLDINGS]: "אחזקות (חיסכון פיננסי)",
  [CLAL_KIND.HEALTH]: "בריאות",
  [CLAL_KIND.LIFE]: "ביטוח חיים",
  [CLAL_KIND.UNKNOWN]: "לא מזוהה",
};
